/**
 * Goal projection for the Account Growth page. Pure and dependency-free: the server uses it to
 * answer GET /api/account-growth, and the client bundles the same function to re-project live
 * as the user edits their goal (so the two can never disagree).
 *
 * Model: monthly compounding at the trader's average monthly trading return, plus an optional
 * fixed monthly contribution — balance(n+1) = balance(n) × (1 + r) + contribution.
 */

export interface ProjectionInput {
  currentBalance: number;
  goalAmount: number;
  /** Average monthly trading return, in percent (e.g. 2.5 for +2.5%/month). */
  monthlyReturnPercent: number;
  monthlyContribution?: number;
  /** Projection start (defaults to now). */
  from?: Date;
  /** Horizon cap in months (default 600 = 50 years). */
  maxMonths?: number;
}

export interface ProjectionResult {
  /** 'reached' already at/above goal; 'projected' reaches it within the horizon; 'unreachable' otherwise. */
  status: "reached" | "projected" | "unreachable";
  months: number | null;
  /** 'YYYY-MM' month the goal is reached, when projected. */
  targetMonth: string | null;
  /** Month-end projected balances from `from`, up to the target month (or 60 months if unreachable). */
  path: { month: string; balance: number }[];
}

const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

export const projectGoal = (input: ProjectionInput): ProjectionResult => {
  const r = input.monthlyReturnPercent / 100;
  const contribution = input.monthlyContribution || 0;
  const maxMonths = input.maxMonths ?? 600;
  const from = input.from ?? new Date();
  const start = new Date(from.getFullYear(), from.getMonth(), 1);

  if (!(input.goalAmount > 0) || !Number.isFinite(input.goalAmount)) {
    return { status: "unreachable", months: null, targetMonth: null, path: [] };
  }
  if (input.currentBalance >= input.goalAmount) {
    return { status: "reached", months: 0, targetMonth: monthKey(start), path: [] };
  }

  const path: { month: string; balance: number }[] = [];
  let balance = input.currentBalance;
  for (let n = 1; n <= maxMonths; n++) {
    balance = balance * (1 + r) + contribution;
    const month = monthKey(new Date(start.getFullYear(), start.getMonth() + n, 1));
    path.push({ month, balance });
    if (balance >= input.goalAmount) {
      return { status: "projected", months: n, targetMonth: month, path: thin(path) };
    }
    // A shrinking balance can never reach a goal above it.
    if (balance <= 0) {
      break;
    }
  }
  return { status: "unreachable", months: null, targetMonth: null, path: path.slice(0, 60) };
};

/** Keep chart paths small: at most ~120 points, always including the last. */
const thin = (path: { month: string; balance: number }[]) => {
  if (path.length <= 120) {
    return path;
  }
  const step = Math.ceil(path.length / 120);
  return path.filter((_, i) => i % step === 0 || i === path.length - 1);
};
